/**
 * Unified API client.
 *
 * All backend responses follow the structure:
 *
 *   Success: { "success": true,  "data": <value>, "error": null }
 *   Error:   { "success": false, "data": null,    "error": { "code": "...", "message": "..." } }
 *
 * This module provides a thin wrapper around fetch that:
 * 1. Attaches auth headers (Bearer JWT for user endpoints)
 * 2. Parses the unified response envelope
 * 3. Throws typed errors on failure
 */

const ABSOLUTE_URL_RE = /^https?:\/\//i
const apiBase = normalizeApiBase(process.env.VUE_APP_API_URL || '/api')

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  code: string
  message: string
  paywall?: {
    currentPoints: number
    requiredPoints: number
    retailYuanPerPage: number
  }
  [key: string]: unknown
}

export interface ApiResponse<T = unknown> {
  success: boolean
  data: T | null
  error: ApiErrorBody | null
}

export class ApiError extends Error {
  code: string
  status: number
  body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `API error (${status})`)
    this.name = 'ApiError'
    this.code = body.code || 'UNKNOWN'
    this.status = status
    this.body = body
  }
}

export class QuotaError extends ApiError {
  paywall: NonNullable<ApiErrorBody['paywall']>

  constructor(status: number, body: ApiErrorBody) {
    super(status, body)
    this.name = 'QuotaError'
    this.paywall = body.paywall || { currentPoints: 0, requiredPoints: 0, retailYuanPerPage: 1.9 }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeApiBase(base: string): string {
  const normalized = base.replace(/\/+$/, '')
  return normalized || '/api'
}

export function resolveApiUrl(path: string): string {
  if (ABSOLUTE_URL_RE.test(path)) return path

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  if (normalizedPath === apiBase || normalizedPath.startsWith(`${apiBase}/`)) {
    return normalizedPath
  }
  if (apiBase.endsWith('/api') && normalizedPath.startsWith('/api/')) {
    return `${apiBase.slice(0, -4)}${normalizedPath}`
  }
  return `${apiBase}${normalizedPath}`
}

function tokenFromStorage(): string | null {
  return localStorage.getItem('ppt_token')
}

function authHeaders(): Record<string, string> {
  const token = tokenFromStorage()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

export async function request<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = authHeaders()
  const response = await fetch(resolveApiUrl(path), {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
  })

  const result: ApiResponse<T> = await response.json()

  if (!result.success || !response.ok) {
    const errBody = result.error || { code: 'UNKNOWN', message: `HTTP ${response.status}` }
    if (response.status === 402 && errBody.paywall) {
      throw new QuotaError(response.status, errBody)
    }
    throw new ApiError(response.status, errBody)
  }

  // data is guaranteed non-null on success
  return result.data as T
}

/** Convenience: POST with JSON body */
export function post<T = unknown>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Convenience: GET */
export function get<T = unknown>(path: string): Promise<T> {
  return request<T>(path)
}

/**
 * Upload a file via multipart/form-data.
 * Does NOT set Content-Type so the browser can set the correct multipart boundary.
 */
export async function uploadFile<T = unknown>(path: string, file: File): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  const token = tokenFromStorage()
  const response = await fetch(resolveApiUrl(path), {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  })
  const result: ApiResponse<T> = await response.json()
  if (!result.success || !response.ok) {
    const errBody = result.error || { code: 'UNKNOWN', message: `HTTP ${response.status}` }
    throw new ApiError(response.status, errBody)
  }
  return result.data as T
}

/**
 * Presigned PUT 直传对象存储。不经过 /api，走 bucket CORS。
 * contentType 必须与后端签名 presigned URL 时使用的完全一致
 * （Content-Type 计入 OSS string-to-sign，不一致必 403），
 * 因此调用方应传 upload-ticket 返回的 contentType。
 */
export async function putFileToUrl(url: string, file: File, contentType?: string): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
  })
  if (!response.ok) {
    throw new Error(`直传失败（HTTP ${response.status}）`)
  }
}
